import { Injectable } from '@angular/core';
import { CacPromptDialogComponent, DialogExtended, DialogService } from '@controladad/ng-base';
import {
  AdminUserDialogComponent,
  AdminUserDialogData,
  AdminUserDialogResult,
} from './admin-user-dialog/admin-user-dialog.component';
import {
  CustomerUserDialogComponent,
  CustomerUserDialogData,
  CustomerUserDialogResult,
} from './customer-user-dialog/customer-user-dialog.component';
import {
  VendorUserDialogComponent,
  VendorUserDialogData,
  VendorUserDialogResult,
} from './vendor-user-dialog/vendor-user-dialog.component';
import {
  CustomerAddressDialogData,
  CustomerAddressDialogComponent,
  CustomerAddressDialogResult,
} from './customer-address-dialog/customer-address-dialog.component';
import {
  WalletTransactionDialogComponent,
  WalletTransactionDialogData,
  WalletTransactionDialogResult,
} from './wallet-transaction-dialog/wallet-transaction-dialog.component';
import {
  AttributeDialogComponent,
  AttributeDialogData,
  AttributeDialogResult,
} from './attribute-dialog/attribute-dialog.component';
import { BrandDialogComponent, BrandDialogData, BrandDialogResult } from './brand-dialog/brand-dialog.component';
import {
  ProductDialogComponent,
  ProductDialogData,
  ProductDialogResult,
} from './product-dialog/product-dialog.component';
import { UploadImageDialogComponent } from './product-image-upload-dialog/product-image-upload-dialog';
import { OrderedProductImage } from '@models';
import { BannerDialogComponent, BannerDialogData, BannerDialogResult } from './banner-dialog/banner-dialog.component';
import {
  AttributeSetDialogComponent,
  AttributeSetDialogData,
  AttributeSetDialogResult,
} from './attribute-set-dialog/attribute-set-dialog.component';
import {
  CouponDialogComponent,
  CouponDialogData,
  CouponDialogResult,
} from './coupon-dialog/coupon-dialog.component';
import {
  OrderDetailDialogComponent,
  OrderDetailDialogData,
  OrderDetailDialogResult,
} from './order-detail-dialog/order-detail-dialog.component';
import {
  VendorOrderDetailDialogComponent,
  VendorOrderDetailDialogData,
  VendorOrderDetailDialogResult,
} from './vendor-order-detail-dialog/vendor-order-detail-dialog.component';
import { BlogDialogComponent } from './blog-dialog/blog-dialog.component';
import { EditorDialogComponent, EditorDialogData, EditorDialogResult } from './editor-dialog/editor-dialog.component';
import { TagDialogComponent } from './tag-dialog/tag-dialog.component';
import { ImageUploadDialogComponent, ImageUploadDialogData, ImageUploadDialogResult } from './image-upload-dialog/image-upload-dialog.component';
import {
  GovernorateDialogComponent,
  GovernorateDialogData,
  GovernorateDialogResult,
} from './governorate-dialog/governorate-dialog.component';
import {
  AreaDialogComponent,
  AreaDialogData,
  AreaDialogResult,
} from './area-dialog/area-dialog.component';
import {
  AddOrderDialogComponent,
  AddOrderDialogData,
  AddOrderDialogResult,
} from './add-order-dialog/add-order-dialog.component';
import {
  RailDialogComponent,
  RailDialogData,
  RailDialogResult,
} from './rail-dialog/rail-dialog.component';
import {
  OrderItemEditDialogComponent,
  OrderItemEditDialogData,
  OrderItemEditDialogResult,
} from './order-item-edit-dialog/order-item-edit-dialog.component';
import {
  OrderItemAddDialogComponent,
  OrderItemAddDialogData,
  OrderItemAddDialogResult,
} from './order-item-add-dialog/order-item-add-dialog.component';
import {
  RefundConfirmationDialogComponent,
  RefundConfirmationDialogData,
  RefundConfirmationDialogResult,
} from './refund-confirmation-dialog/refund-confirmation-dialog.component';
import {
  InvoiceDateDialogComponent,
  InvoiceDateDialogData,
  InvoiceDateDialogResult,
} from './invoice-date-dialog/invoice-date-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class DialogExtendedService extends DialogService {
  override deletePrompt(multiple?: boolean, opts?: {
    itemName?: string;
    title?: string;
    message?: string;
    yesButtonText?: string;
    noButtonText?: string;
  }): DialogExtended<CacPromptDialogComponent, boolean> {
    return super.deletePrompt(multiple, {
      itemName: opts?.itemName || 'Item',
      title: opts?.title,
      message: opts?.message,
      yesButtonText: opts?.yesButtonText || `Yes, Continue`,
      noButtonText: opts?.noButtonText || `No, Cancel`,
    }).setActionType('delete');
  }

  /**
   * Shows a custom refund to wallet confirmation dialog
   * @param data Optional custom message
   * @returns Dialog reference with afterClosed() observable that returns true (refund), false (no refund), or null (cancelled)
   */
  refundConfirmation(data?: RefundConfirmationDialogData) {
    return this.dialog.open<RefundConfirmationDialogComponent, RefundConfirmationDialogData, RefundConfirmationDialogResult>(
      RefundConfirmationDialogComponent,
      data ?? {},
      {
        width: '500px',
        maxWidth: '90vw',
        disableClose: false,
      }
    );
  }

  /**
   * Shows invoice date override dialog
   * @param data Optional custom message
   * @returns Dialog reference with afterClosed() observable that returns { overrideDate: Date | null } or null if cancelled
   */
  invoiceDate(data?: InvoiceDateDialogData) {
    return this.dialog.open<InvoiceDateDialogComponent, InvoiceDateDialogData, InvoiceDateDialogResult>(
      InvoiceDateDialogComponent,
      data ?? {},
      {
        width: '500px',
        maxWidth: '90vw',
        disableClose: false,
      }
    );
  }

  adminUser(data: AdminUserDialogData) {
    return this.dialog.open<AdminUserDialogComponent, AdminUserDialogData, AdminUserDialogResult>(
      AdminUserDialogComponent,
      data,
      {
        minWidth: '30rem',
        width: '70%',
        maxWidth: '42rem',
      },
    );
  }

  customerUser(data: CustomerUserDialogData) {
    return this.dialog.open<CustomerUserDialogComponent, CustomerUserDialogData, CustomerUserDialogResult>(
      CustomerUserDialogComponent,
      data,
      {
        minWidth: '80vw',
        width: '80vw',
        minHeight: '80%',
        height: '80%',
      },
    );
  }

  vendorUser(data: VendorUserDialogData) {
    return this.dialog.open<VendorUserDialogComponent, VendorUserDialogData, VendorUserDialogResult>(
      VendorUserDialogComponent,
      data,
      {
        minWidth: '80vw',
        width: '80vw',
        minHeight: '70%',
        height: '70%',
      },
    );
  }

  customerAddress(data: CustomerAddressDialogData) {
    return this.dialog.open<CustomerAddressDialogComponent, CustomerAddressDialogData, CustomerAddressDialogResult>(
      CustomerAddressDialogComponent,
      data,
      {
        minWidth: '70vw',
        width: '70vw',
        minHeight: '70%',
        height: '70%',
      },
    );
  }

  attribute(data: AttributeDialogData) {
    return this.dialog.open<AttributeDialogComponent, AttributeDialogData, AttributeDialogResult>(
      AttributeDialogComponent,
      data,
      {
        minWidth: '70vw',
        width: '70vw',
        minHeight: '80%',
        height: '80%',
      },
    );
  }

  attributeSet(data: AttributeSetDialogData) {
    return this.dialog.open<AttributeSetDialogComponent, AttributeSetDialogData, AttributeSetDialogResult>(
      AttributeSetDialogComponent,
      data,
    );
  }

  brand(brand: BrandDialogData) {
    return this.dialog.open<BrandDialogComponent, BrandDialogData, BrandDialogResult>(BrandDialogComponent, brand, {
      minWidth: '700px',
      width: '700px',
    });
  }

  product(product: ProductDialogData) {
    return this.dialog.open<ProductDialogComponent, ProductDialogData, ProductDialogResult>(
      ProductDialogComponent,
      product,
      {
        width: '90vw',
        maxWidth: '1000px',
        minHeight: '90%',
        height: '90%',
      },
    );
  }



  banner(data?: BannerDialogData) {
    return this.dialog.open<BannerDialogComponent, BannerDialogData, BannerDialogResult>(
      BannerDialogComponent,
      data ?? {},
      {
        minWidth: '50vw',
        maxWidth: '95vw',
        minHeight: '50vh',
        maxHeight: '95vh',
      });
  }


  productImage() {
    return this.dialog.open<UploadImageDialogComponent, undefined, OrderedProductImage>(UploadImageDialogComponent);
  }

  coupon(data: CouponDialogData) {
    return this.dialog.open<CouponDialogComponent, CouponDialogData, CouponDialogResult>(
      CouponDialogComponent,
      data,
      {
        minWidth: '800px',
        minHeight: '70%',
        height: '70%',
      },
    );
  }

  orderDetail(data: OrderDetailDialogData) {
    return this.dialog.open<OrderDetailDialogComponent, OrderDetailDialogData, OrderDetailDialogResult>(
      OrderDetailDialogComponent,
      data,
      {
        minWidth: '90vw',
        width: '90vw',
        maxWidth: '1200px',
        minHeight: '85%',
        height: '85%',
      },
    );
  }

  vendorOrderDetail(data: VendorOrderDetailDialogData) {
    return this.dialog.open<VendorOrderDetailDialogComponent, VendorOrderDetailDialogData, VendorOrderDetailDialogResult>(
      VendorOrderDetailDialogComponent,
      data,
      {
        minWidth: '90vw',
        width: '90vw',
        maxWidth: '1200px',
        minHeight: '85%',
        height: '85%',
      },
    );
  }

  walletTransaction(data: WalletTransactionDialogData) {
    return this.dialog.open<WalletTransactionDialogComponent, WalletTransactionDialogData, WalletTransactionDialogResult>(
      WalletTransactionDialogComponent,
      data,
      {
        minWidth: '500px',
        width: '600px',
        maxWidth: '90vw',
      },
    );
  }

  blog(data?: any) {
    return this.dialog.open<any, any, any>(
      BlogDialogComponent,
      data ?? {},
      {
        minWidth: '50vw',
        maxWidth: '95vw',
        minHeight: '50vh',
        maxHeight: '95vh',
      });
  }

  editor(data: EditorDialogData) {
    return this.dialog.open<EditorDialogComponent, EditorDialogData, EditorDialogResult>(EditorDialogComponent, data, {
      disableClose: true,
      minWidth: '90vw',
      height: '80vh',
    });
  }

  tag(data?: any) {
    return this.dialog.open<any, any, any>(
      TagDialogComponent,
      data ?? {},
      {
        minWidth: '50vw',
        maxWidth: '95vw',
        minHeight: '50vh',
        maxHeight: '95vh',
      });
  }

  imageUpload(data?: ImageUploadDialogData) {
    return this.dialog.open<ImageUploadDialogComponent, ImageUploadDialogData, ImageUploadDialogResult>(ImageUploadDialogComponent, data, {
      minHeight: '17rem',
      maxWidth: '90vw',
      maxHeight: '90vh',
    });
  }

  governorate(data: GovernorateDialogData) {
    return this.dialog.open<GovernorateDialogComponent, GovernorateDialogData, GovernorateDialogResult>(
      GovernorateDialogComponent,
      data,
      {
        width: '900px',
        maxWidth: '90vw',
        minHeight: '600px',
        maxHeight: '90vh',
      }
    );
  }

  area(data: AreaDialogData) {
    return this.dialog.open<AreaDialogComponent, AreaDialogData, AreaDialogResult>(
      AreaDialogComponent,
      data,
      {
        minWidth: '500px',
        width: '500px',
      }
    );
  }

  addOrder(data: AddOrderDialogData) {
    return this.dialog.open<AddOrderDialogComponent, AddOrderDialogData, AddOrderDialogResult>(
      AddOrderDialogComponent,
      data,
      {
        width: '1000px',
        maxWidth: '95vw',
        minHeight: '700px',
        maxHeight: '95vh',
        disableClose: true,
      }
    );
  }

  rail(data?: RailDialogData) {
    return this.dialog.open<RailDialogComponent, RailDialogData, RailDialogResult>(
      RailDialogComponent,
      data ?? {},
      {
        width: '1200px',
        maxWidth: '90vw',
        minHeight: '90vh',
        maxHeight: '90vh',
      }
    );
  }

  orderItemEdit(data: OrderItemEditDialogData) {
    return this.dialog.open<OrderItemEditDialogComponent, OrderItemEditDialogData, OrderItemEditDialogResult>(
      OrderItemEditDialogComponent,
      data,
      {
        width: '400px',
        maxWidth: '90vw',
      }
    );
  }

  orderItemAdd(data: OrderItemAddDialogData) {
    return this.dialog.open<OrderItemAddDialogComponent, OrderItemAddDialogData, OrderItemAddDialogResult>(
      OrderItemAddDialogComponent,
      data,
      {
        width: '700px',
        maxWidth: '90vw',
        minHeight: '600px',
      }
    );
  }
}













